import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Lock, Check, ArrowRight, Sparkles, Calendar, ChevronRight, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const VisionPlan = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [planStatus, setPlanStatus] = useState(null);
  const [currentDayInfo, setCurrentDayInfo] = useState(null);
  const [showProjection, setShowProjection] = useState(false);
  const [completingDay, setCompletingDay] = useState(false);

  useEffect(() => {
    fetchPlanStatus();
  }, [token]);

  const fetchPlanStatus = async () => {
    try {
      const res = await axios.get(`${API}/plan/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanStatus(res.data);
      
      // If plan started, get current day info
      if (res.data.started && res.data.current_day > 0) {
        const dayRes = await axios.get(`${API}/plan/day/${res.data.current_day}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentDayInfo(dayRes.data);
      } else if (!res.data.started) {
        // Show projection screen for new users
        setShowProjection(true);
      }
    } catch (err) {
      console.error('Error fetching plan status:', err);
      toast.error('Erreur lors du chargement du plan');
    } finally {
      setLoading(false);
    }
  };

  const startPlan = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/plan/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPlanStatus(prev => ({ ...prev, started: true, current_day: 1 }));
      setCurrentDayInfo({ day: res.data.day, is_current: true, is_accessible: true });
      setShowProjection(false);
      toast.success('🚀 Ton plan Vision Lab commence maintenant !');
    } catch (err) {
      toast.error('Erreur lors du démarrage du plan');
    } finally {
      setLoading(false);
    }
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const completeDay = async () => {
    if (!currentDayInfo?.day?.day) return;
    
    try {
      setCompletingDay(true);
      const res = await axios.post(`${API}/plan/complete-day`, {
        day: currentDayInfo.day.day,
        validation_type: 'done'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success modal with emotional message
      setSuccessData(res.data);
      setShowSuccessModal(true);
      
      // Refresh plan status after a short delay
      setTimeout(async () => {
        await fetchPlanStatus();
      }, 500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setCompletingDay(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
  };

  const goToTool = () => {
    if (currentDayInfo?.day?.tool_path) {
      navigate(currentDayInfo.day.tool_path);
    }
  };

  // Calculate progress
  const progress = planStatus ? (planStatus.completed_days?.length || 0) / 30 * 100 : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  // Projection Screen - Before starting the plan
  if (showProjection || !planStatus?.started) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg w-full text-center space-y-8"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A57A03] flex items-center justify-center"
            >
              <Sparkles className="text-black" size={32} />
            </motion.div>
            
            {/* Title */}
            <div>
              <h1 className="font-serif text-3xl text-white mb-2">
                Ton plan Vision Lab
              </h1>
              <p className="text-[#D4AF37] text-xl">30 jours</p>
            </div>
            
            {/* Promise */}
            <div className="card-premium p-6 text-left space-y-4">
              <p className="text-[#E5C585] leading-relaxed">
                En 30 jours, tu vas :
              </p>
              <ul className="space-y-3 text-[#A1A1AA]">
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] mt-1">✦</span>
                  <span><strong className="text-white">Clarifier ton message</strong> pour que les bonnes personnes te comprennent instantanément</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] mt-1">✦</span>
                  <span><strong className="text-white">Structurer ta marque</strong> pour arrêter de poster sans direction</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#D4AF37] mt-1">✦</span>
                  <span><strong className="text-white">Créer une présence magnétique</strong> qui attire naturellement tes clientes idéales</span>
                </li>
              </ul>
            </div>
            
            {/* Coach message */}
            <div className="text-[#A1A1AA] text-sm leading-relaxed">
              <p>Je vais te guider chaque jour.</p>
              <p className="text-[#D4AF37]">Tu n'as rien à deviner.</p>
            </div>
            
            {/* CTA */}
            <Button
              onClick={startPlan}
              className="btn-gold py-4 px-8 text-base"
              disabled={loading}
            >
              COMMENCER MAINTENANT
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  // Plan Complete Screen
  if (planStatus?.completed_days?.length === 30) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg w-full text-center space-y-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A57A03] flex items-center justify-center"
            >
              <Trophy className="text-black" size={40} />
            </motion.div>
            
            <div>
              <h1 className="font-serif text-3xl text-white mb-2">
                Félicitations !
              </h1>
              <p className="text-[#E5C585]">Tu as terminé ton plan 30 jours</p>
            </div>
            
            <div className="card-gold p-6 text-left space-y-4">
              <p className="text-white">Regarde le chemin que tu as parcouru :</p>
              <ul className="space-y-2 text-[#E5C585]">
                <li>✓ Message clarifié</li>
                <li>✓ Marque structurée</li>
                <li>✓ Identité visuelle créée</li>
                <li>✓ Contenu activé</li>
              </ul>
            </div>
            
            <p className="text-[#A1A1AA]">
              Ce que tu as fait en 30 jours était la base.<br/>
              <span className="text-[#D4AF37]">Sur 90 jours, on va transformer ça en vraie croissance.</span>
            </p>
            
            <Button
              onClick={() => navigate('/journey-evolution')}
              className="btn-gold py-4 px-8"
            >
              CONTINUER MON ÉVOLUTION
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  // Current Day View - The main guided experience
  const day = currentDayInfo?.day;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-8">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-[#71717A]">
            <span>Jour {planStatus?.current_day || 1} / 30</span>
            <span>{Math.round(progress)}% complété</span>
          </div>
          <div className="h-1 bg-[#1C1C1C] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C585]"
            />
          </div>
        </div>

        {/* Current Day Card */}
        <AnimatePresence mode="wait">
          {day && (
            <motion.div
              key={day.day}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Day Header */}
              <div className="card-gold p-6 text-center">
                <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 px-3 py-1 rounded-full mb-4">
                  <Calendar size={14} className="text-[#D4AF37]" />
                  <span className="text-[#D4AF37] text-xs font-accent tracking-wider">
                    JOUR {day.day} — {day.theme.toUpperCase()}
                  </span>
                </div>
                
                <h1 className="font-serif text-2xl text-white mb-3">
                  {day.title}
                </h1>
                
                <p className="text-[#A1A1AA] leading-relaxed">
                  {day.description}
                </p>
              </div>

              {/* Why Section */}
              <div className="card-premium p-5 border-l-2 border-[#D4AF37]">
                <p className="text-[#71717A] text-xs uppercase tracking-wider mb-1">Pourquoi ?</p>
                <p className="text-[#E5C585]">
                  Pour que les bonnes personnes comprennent immédiatement ce que tu fais et veuillent en savoir plus.
                </p>
              </div>

              {/* Main CTA */}
              <Button
                onClick={goToTool}
                className="w-full btn-gold py-5 text-base flex items-center justify-center gap-2"
              >
                <Sparkles size={18} />
                {day.cta_label}
                <ArrowRight size={18} />
              </Button>

              {/* Validation Section */}
              <div className="card-premium p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full border border-[#D4AF37] flex items-center justify-center">
                    <ChevronRight size={16} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-white">{day.action_text}</p>
                </div>
                
                <Button
                  onClick={completeDay}
                  disabled={completingDay}
                  className="w-full bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 py-4 flex items-center justify-center gap-2"
                >
                  {completingDay ? (
                    <div className="animate-spin w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Check size={18} />
                      {day.validation_label}
                    </>
                  )}
                </Button>
              </div>

              {/* Upcoming Days Preview (locked) */}
              {planStatus?.current_day < 30 && (
                <div className="space-y-3">
                  <p className="text-[#52525B] text-xs uppercase tracking-wider">Prochaines étapes</p>
                  {[1, 2].map(offset => {
                    const nextDay = planStatus.plan_days?.[planStatus.current_day + offset - 1];
                    if (!nextDay) return null;
                    
                    return (
                      <div 
                        key={nextDay.day}
                        className="card-premium p-4 flex items-center gap-4 opacity-50"
                      >
                        <div className="w-10 h-10 rounded-full bg-[#1C1C1C] flex items-center justify-center">
                          <Lock size={14} className="text-[#52525B]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[#71717A] text-xs">Jour {nextDay.day}</p>
                          <p className="text-[#A1A1AA] text-sm">{nextDay.title}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success Modal - Emotional validation */}
        <AnimatePresence>
          {showSuccessModal && successData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeSuccessModal}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-8 max-w-md w-full text-center space-y-6"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Success Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A57A03] flex items-center justify-center"
                >
                  <Check className="text-black" size={32} />
                </motion.div>
                
                {/* Emotional Message */}
                <div className="space-y-3">
                  <p className="text-white text-lg leading-relaxed whitespace-pre-line">
                    {successData.message}
                  </p>
                </div>
                
                {/* Projection */}
                {successData.projection && (
                  <div className="border-t border-[#D4AF37]/20 pt-4">
                    <p className="text-[#E5C585] text-sm leading-relaxed">
                      {successData.projection}
                    </p>
                  </div>
                )}
                
                {/* CTA */}
                <Button
                  onClick={closeSuccessModal}
                  className="btn-gold py-3 px-8"
                >
                  On continue
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default VisionPlan;
